import { useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

// local imports
import './OfficePlanSideBar.scss'
import binaGray from 'assets/icons/bina-gray.svg'
import masaGray from 'assets/icons/masa-gray.svg'
import locationMarkerGray from 'assets/icons/location-marker-gray.svg'
import calendarGray from 'assets/icons/calendar-gray.svg'
import warningImage from 'assets/images/warning-image.svg'
import { PlanTypes } from 'types/BaseType'
import { ISideBarModel, ViewSideBar } from 'types/CalendarType'
import AsyncImage from 'components/ui/AsyncImage'

const OfficePlanSideBar = (props: {
  model: ISideBarModel
  onClickDeleteOfficePlan: (arg0: any) => any
  onClickRedirectOfficePlan: (arg0: any) => any
}) => {
  const [displayDialog, setDisplayDialog] = useState(false)
  /**
   * VIEW
   * EDIT
   * CANCEL
   */

  const [height, setHeight] = useState(window.innerHeight)
  const { t } = useTranslation()
  const updateDimensions = () => {
    setHeight(window.innerHeight)
  }

  const formatDate = (date: Date) => {
    if (date === null || date === undefined) return ''
    let res = moment(date)
    return res.format('DD.MM.YYYY')
  }

  useEffect(() => {
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  const deleteOfficePlan = () => {
    props?.onClickDeleteOfficePlan(props?.model)
    setDisplayDialog(false)
  }

  const onHide = () => {
    setDisplayDialog(false)
  }

  const renderFooter = () => {
    return (
      <div className="d-flex flex-row of-confirm-footer">
        <Button label={t('buttons.no')} onClick={onHide} className="p-button-outlined w-100" />
        <Button
          label={t('buttons.yes')}
          onClick={() => {
            deleteOfficePlan()
          }}
          className="w-100 of-button-primary"
          disabled={false}
        />
      </div>
    )
  }

  const confirmDialogUI = () => {
    return (
      <Dialog
        header=""
        visible={displayDialog}
        position={'center'}
        modal
        style={{ width: '400px' }}
        footer={renderFooter()}
        onHide={onHide}
        draggable={false}
        resizable={false}>
        <div className="d-flex flex-column of-newplan-confirm-dialog">
          <div className="of-confirm-image">
            <img src={warningImage} alt="" />
          </div>
          <div className="">
            <p className="of-confirm-info mt-2">{t('overlaypanel-texts.cancel-process')}</p>
          </div>
          <div className="of-confirm-detail">
            <p>{t('overlaypanel-texts.cancel-text', { plantype: getPlanTypeText() })}</p>
            <p>
              <strong>{t('input-texts.date')}:</strong> {formatDate(props?.model?.day?.date)}
            </p>
          </div>
        </div>
      </Dialog>
    )
  }

  const getHeader = () => {
    let { model } = props
    let planType = model?.event?.planType
    if (planType) {
      if (planType === PlanTypes.TableReservation || planType === PlanTypes.TableFloorReservation) {
        return t('reservation-tab.office-not2')
      } else if (
        planType === PlanTypes.OtherLocationTableReservation ||
        planType === PlanTypes.OtherLocationTableFloorReservation
      ) {
        return t('reservation-tab.shared-not')
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const getHeaderInfo = () => {
    let { model } = props
    let planType = model?.event?.planType
    if (planType) {
      if (planType === PlanTypes.TableReservation) {
        return t('reservation-tab.office-info2')
      } else if (planType === PlanTypes.OtherLocationTableReservation) {
        return t('reservation-tab.shared-info')
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const getOfficeLabel = () => {
    let { model } = props
    let planType = model?.event?.planType
    if (planType) {
      if (planType === PlanTypes.TableReservation) {
        return t('reservation-tab.office-not2').toLocaleLowerCase()
      } else if (planType === PlanTypes.OtherLocationTableReservation) {
        return t('reservation-tab.shared-not').toLocaleLowerCase()
      }
    }
    return t('reservation-tab.office-not2').toLocaleLowerCase()
  }

  const getPlanTypeText = () => {
    let { model } = props
    let planType = model?.event?.planType
    if (planType) {
      if (planType === PlanTypes.TableReservation) {
        return t('plantype.work-from-office')
      } else if (planType === PlanTypes.OtherLocationTableReservation) {
        return t('plantype.shared-office')
      }
    }
    return t('plantype.work-from-office')
  }

  const getCalculatedHeight = () => {
    if (height) {
      let value = height - 90
      if (height < 400) return 600
      return value
    }
  }

  return (
    <>
      <div className="of-office-sidebar-container " style={{ height: getCalculatedHeight() }}>
        <div className="text-center">
          <p className="of-sidebar-header">{getHeader()}</p>
          <p className="of-sidebar-description">
            {t('squads-event.cancel-text-part1')} {getOfficeLabel()} {t('squads-event.cancel-text-part2')}
          </p>
        </div>
        <p className="mt-2 of-plandate-wheretowork">{getHeaderInfo()}</p>
        <div className="of-office-sidebar-personnel d-flex flex-row mb-3">
          <div className="d-flex align-items-center">
            <AsyncImage className={'of-office-photo'} src={props?.model?.user?._photoUrl} alt="" />
          </div>
          <div className="p-2">
            <div className={'of-name'}>{props?.model?.user?.fullName}</div>
            <div className={'of-title'}>{props?.model?.user?.positionName}</div>
          </div>
        </div>

        <div className="of-plandate mt-1">
          <p>
            <img className="of-office-plan-icon" src={calendarGray} alt="" />
            <span>{t('input-texts.date')}: </span>
            {formatDate(props?.model?.event?.reservationDate)}
          </p>
        </div>
        <div className="of-plandate mt-2">
          <p>
            <img className="of-office-plan-icon" src={locationMarkerGray} alt="" />
            <span> {t('most-used-words.location')}: </span>{' '}
            {props?.model?.event?.locationFirmName + ', ' + props?.model?.event?.locationName}
          </p>
        </div>
        <div className="d-flex of-plan-location-block">
          <img src={binaGray} alt="" />
          <p>
            <span>{t('most-used-words.block')}: </span>
            {props?.model?.event?.blockName}
            <br />
            <span>{t('most-used-words.floor')}: </span>
            {props?.model?.event?.floorName}
          </p>
        </div>
        <div className="of-plandate">
          <p>
            <img className="of-office-plan-icon" src={masaGray} alt="" />
            <span>{t('calendar.table-no')}: </span> {props?.model?.event?.tableNum}
          </p>
        </div>

        {props?.model?.view === ViewSideBar.EDIT ? (
          <div className="of-office-plan-bottom-buttons">
            <div className="row ">
              <div
                className={
                  props.model?.event?.planType === 5 || props.model?.event?.planType === 6 ? 'col-md-12' : 'col-md-6'
                }>
                <Button
                  className="p-button-outlined"
                  label={t('buttons.cancel-notification')}
                  onClick={(e) => {
                    setDisplayDialog(true)
                  }}
                />
              </div>
              {!(props.model?.event?.planType === 5 || props.model?.event?.planType === 6) && (
                <div className="col-md-6">
                  <Button
                    label={t('buttons.go-to-office')}
                    onClick={(e) => {
                      props?.onClickRedirectOfficePlan(props?.model)
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : null}

        {props?.model?.view === ViewSideBar.CANCEL ? (
          <div className="of-office-plan-bottom-buttons mt-2">
            <div className="row ">
              {!(props.model?.event?.planType === 5 || props.model?.event?.planType === 6) && (
                <div className="col-md-12">
                  <Button
                    label={t('buttons.go-to-office')}
                    onClick={(e) => {
                      props?.onClickRedirectOfficePlan(props?.model)
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : null}

        {confirmDialogUI()}
      </div>
    </>
  )
}
export default OfficePlanSideBar
